import type { ReactNode } from "react";
import { cn } from "@racoon/ui";
import { enumColors } from "@racoon/lib/src/enum-colors";
import { EMPTY_CELL, isUndefinedEnum } from "@racoon/lib";
import { Namespace, TranslationKey } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";

/** A user's name. A bot-made user carries the whole name in `firstName`; then `lastName` stays out. */
export const fullName = (u?: Pick<ConnectProto.User, "firstName" | "lastName"> | null): string => {
  const first = u?.firstName?.trim() ?? "";
  if (first.includes(" ")) return first;
  return [first, u?.lastName?.trim()].filter(Boolean).join(" ");
};

/** First letters of the first two words, or the first two letters of a one-word name. */
export const getInitials = (name: string): string => {
  const [first = "—", second] = name.split(/\s+/).filter(Boolean);
  return (second ? first[0] + second[0] : first.substring(0, 2)).toUpperCase();
};

/**
 * An UNDEFINED enum value (a NULL or unknown value on the server) is never shown as a
 * badge: it renders `empty` instead — the table's EMPTY_CELL placeholder by default,
 * or pass `{ empty: null }` where a placeholder would be noise (a header chip).
 * `_undefined` is what `ENUM_${Enum[n]}` yields for a number the client does not know.
 */
export const badge = (
  text: string,
  tone: TranslationKey<Namespace.Enum>,
  { empty = EMPTY_CELL, className }: { empty?: ReactNode; className?: string } = {},
) => {
  if (isUndefinedEnum(tone)) return empty;
  const isJobRelated = tone.includes("JOB_TYPE_") || tone.includes("JOB_STATUS_");
  const isRouteRelated = tone.includes("ROUTE_STATUS_");
  const isTruckRelated =
    tone.includes("TRUCK_STATUS_") ||
    tone.includes("TRUCK_ON_WAY") ||
    tone.includes("TRUCK_WAITING");
  const isReceiverNotified =
    tone.includes("RECEIVER_NOTIFIED") || tone.includes("RECEIVER_NOT_NOTIFIED");
  const isPickupRelated = tone.includes("LOCATION_STATUS");
  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs text-center",
        isRouteRelated
          ? "min-w-[7.5rem] !w-[7.5rem] max-w-[7.5rem]"
          : isJobRelated
            ? "min-w-[7rem] !w-[7rem] max-w-[7rem]"
            : isTruckRelated
              ? "w-auto"
              : isReceiverNotified
                ? "min-w-[7rem] !w-[7rem] max-w-[7rem] min-h-[1.5rem]"
                : isPickupRelated
                  ? "min-w-[7rem] !w-[7rem] max-w-[7rem]"
                  : "min-w-[8.5rem] w-[8.5rem] max-w-[8.5rem]",
        enumColors[tone]?.bg,
        enumColors[tone]?.color,
        enumColors[tone]?.borderColor && `${enumColors[tone]?.borderColor} border`,
        className,
      )}
    >
      <span className="w-full text-center truncate">{text}</span>
    </div>
  );
};

export const formatDateToISO = (date: Date): string => {
  return date.toISOString().split("T")[0];
};
